/**
 * Push notifications Fase 2 (Roger 29-may-2026).
 *
 * Pide permiso al usuario, suscribe al SW al servidor de push del browser,
 * y envía la subscription al backend (gateway → api-core) para que pueda
 * emitir push web a través de WorkerNotifier (catálogo PUSH_ENABLED).
 */
import Gateway from './clients/Gateway'

class PushNotifications extends Gateway {
  constructor() {
    super()
    this.endpoints = {
      vapidKey: '/push/vapid-public-key',
      subscribe: '/push/subscribe',
      unsubscribe: '/push/unsubscribe',
      test: '/push/test',
    }
  }

  isSupported() {
    return (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    )
  }

  permission() {
    return typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  }

  async getVapidPublicKey() {
    const res = await this.client.get(this.endpoints.vapidKey)
    return res.data?.public_key
  }

  async subscribeToBackend(subscription, workerCode) {
    const json = subscription.toJSON()
    return this.client.post(this.endpoints.subscribe, {
      worker_code: workerCode || null,
      endpoint: subscription.endpoint,
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
      user_agent: navigator.userAgent,
    })
  }

  async unsubscribeFromBackend(endpoint) {
    return this.client.post(this.endpoints.unsubscribe, { endpoint })
  }

  async sendTest(workerCode) {
    return this.client.post(this.endpoints.test, { worker_code: workerCode })
  }

  /**
   * Flow completo: pide permiso → asegura SW registrado → suscribe → backend.
   * Devuelve la PushSubscription o null si el usuario rechazó / no soportado.
   *
   * Roger 29-may-2026: logs explícitos en cada step para diagnóstico.
   */
  async ensureSubscribed(workerCode) {
    const log = (...args) => console.log('[PushNotifications]', ...args)
    if (!this.isSupported()) {
      log('not supported (SW or PushManager or Notification API missing)')
      return null
    }
    let permission = Notification.permission
    log('initial permission =', permission)
    if (permission === 'default') {
      permission = await Notification.requestPermission()
      log('after requestPermission =', permission)
    }
    if (permission !== 'granted') {
      log('permission not granted, abort')
      return null
    }

    const registration = await navigator.serviceWorker.ready
    log('SW ready, scope =', registration.scope)
    let subscription = await registration.pushManager.getSubscription()
    log('existing subscription?', !!subscription)

    if (!subscription) {
      log('fetching VAPID public key…')
      const publicKey = await this.getVapidPublicKey()
      log('VAPID public_key length =', publicKey ? publicKey.length : null)
      if (!publicKey) {
        console.error('[PushNotifications] no VAPID public key from backend, abort')
        return null
      }
      const applicationServerKey = urlBase64ToUint8Array(publicKey)
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        })
        log('pushManager.subscribe OK, endpoint=', subscription.endpoint.substring(0, 60))
      } catch (e) {
        console.error('[PushNotifications] pushManager.subscribe FAILED:', e)
        throw e
      }
    }

    try {
      const resp = await this.subscribeToBackend(subscription, workerCode)
      log('backend subscribe response status =', resp.status)
    } catch (e) {
      console.error(
        '[PushNotifications] subscribeToBackend FAILED:',
        e?.response?.status,
        e?.message,
      )
      throw e
    }
    return subscription
  }

  async unsubscribe() {
    if (!this.isSupported()) return
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) return
    try {
      await this.unsubscribeFromBackend(subscription.endpoint)
    } catch (e) {
      // ignore
    }
    await subscription.unsubscribe()
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; ++i) {
    output[i] = raw.charCodeAt(i)
  }
  return output
}

export default PushNotifications
